import type { FC } from "react";
import { Box, Card, CardContent, TextField, Typography } from "@mui/material";
import {
	EVENT_ENDINGS,
	type EndingResults,
	type EventEnding,
	type EventResult,
} from "../../domain/event.interface";

export const ENDING_LABEL: Record<EventEnding, string> = {
	APPROVED: "Approved",
	REJECTED: "Rejected",
	EXPIRED: "Expired",
	COMPLETED: "Completed",
};

const NO_RESULT = "No result";
/**
 * A stored mapping whose result the form no longer carries. Shown as a state,
 * never as the raw id (PR #1547 review); the save path prunes it (AC4).
 */
const MISSING_RESULT = "Result no longer configured";

/**
 * AC3 — the one-result-per-ending message, or null when every ending has its
 * own. Judged on the id, never the label: two results may share a display
 * (D-07). The wording matches the server's, so a direct call and the screen
 * say the same thing.
 */
export const duplicateEndingMessage = (
	endingResults: EndingResults,
	eventResults: EventResult[],
): string | null => {
	const seen = new Map<string, EventEnding>();
	for (const ending of EVENT_ENDINGS) {
		const id = endingResults[ending];
		if (!id) continue;
		const first = seen.get(id);
		if (first) {
			// Same wording as the server, and like it never the raw id: the
			// configured label, or the two endings when there is no label.
			const display = eventResults.find((result) => result.id === id)?.display;
			return display
				? `Each ending needs its own result — "${display}" is already used for ${ENDING_LABEL[first]}.`
				: `Each ending needs its own result — ${ENDING_LABEL[ending]} uses the same result as ${ENDING_LABEL[first]}.`;
		}
		seen.set(id, ending);
	}
	return null;
};

/**
 * AC4 — drop any ending that points at a result the event no longer carries,
 * so deleting or renaming a result cannot leave a mapping to nothing.
 */
export const pruneEndingResults = (
	endingResults: EndingResults,
	eventResults: EventResult[],
): EndingResults => {
	const ids = new Set(eventResults.map((result) => result.id));
	const pruned: EndingResults = {};
	for (const ending of EVENT_ENDINGS) {
		const id = endingResults[ending];
		if (id && ids.has(id)) pruned[ending] = id;
	}
	return pruned;
};

interface Props {
	endingResults: EndingResults;
	/** The results configured on this event, as the form currently holds them. */
	eventResults: EventResult[];
	onChange: (endingResults: EndingResults) => void;
}

/**
 * SIMS-9276 — which of the event's own results each ending records.
 *
 * Shown by the Events form only while Require Mobile Validation is on (AC5).
 * The chooser is not filtered by what other endings use, so a duplicate can be
 * attempted and refused on save with the AC's own words (AC3).
 */
export const EndingResultsSection: FC<Props> = ({
	endingResults,
	eventResults,
	onChange,
}) => {
	const choose = (ending: EventEnding, id: string): void => {
		const next: EndingResults = { ...endingResults };
		if (id) next[ending] = id;
		else delete next[ending];
		onChange(next);
	};

	return (
		<Card variant="outlined" data-testid="ending-results-section">
			<CardContent>
				<Typography variant="subtitle1">Ending results</Typography>
				<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
					Choose the result each ending records. Leave an ending on No result if
					this event never reaches it.
				</Typography>
				{EVENT_ENDINGS.map((ending) => {
					const slug = ending.toLowerCase();
					const mapped = endingResults[ending];
					const display = mapped
						? (eventResults.find((result) => result.id === mapped)?.display ??
							MISSING_RESULT)
						: NO_RESULT;

					return (
						<Box
							key={ending}
							data-testid={`ending-map-row-${slug}`}
							sx={{
								display: "flex",
								alignItems: "center",
								gap: 2,
								mb: 1.5,
							}}
						>
							<Typography variant="body2" sx={{ width: 100 }}>
								{ENDING_LABEL[ending]}
							</Typography>
							<TextField
								select
								size="small"
								label={`${ENDING_LABEL[ending]} result`}
								value={mapped ?? ""}
								onChange={(e) => choose(ending, e.target.value)}
								// Native on purpose: the Q-6 test id sits on the real <select>
								// and its <option>s are what AC4's cases read.
								SelectProps={{ native: true }}
								inputProps={{ "data-testid": `ending-map-select-${slug}` }}
								InputLabelProps={{ shrink: true }}
								sx={{ minWidth: 240 }}
							>
								<option value="">{NO_RESULT}</option>
								{eventResults.map((result) => (
									<option key={result.id} value={result.id}>
										{result.display}
									</option>
								))}
							</TextField>
							<Typography
								variant="body2"
								color={mapped ? "text.primary" : "text.secondary"}
								data-testid={`ending-map-value-${slug}`}
							>
								{display}
							</Typography>
						</Box>
					);
				})}
			</CardContent>
		</Card>
	);
};
